import type { SupabaseClient } from '@supabase/supabase-js';
import { KF_ICON, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';
import { sendVarioMessage } from '../vario-iframe.js';
import { ensureKfDialogShell, kfEscapeHtml, showKfDialogResult, KF_DLG_ICON } from './dialog-shell.js';
import {
  createUninstallState, setPurgeChecked, pressPrimary, primaryLabel,
  type UninstallState,
} from '../lib/uninstall-flow.js';

interface UninstallContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { tenantId: string };
}

interface UninstallResult {
  ok: boolean;
  removed_credential_secret: boolean;
  removed_offline_token_secret: boolean;
  removed_storefronts_count: number;
}

interface PurgePreview {
  orders: number;
  order_units: number;
  units: number;
  listings: number;
  storefronts: number;
  article_mappings: number;
  total_rows: number;
}

/**
 * Vario opens this slot after the user clicked "Deinstallieren".
 *
 * **Der Slot laeuft NICHT mehr von allein durch.** Er zeigt zuerst die Auswahl, ob die
 * Kaufland-Daten mitgeloescht werden sollen, und wartet auf den Klick. Ohne Haekchen ist
 * das Verhalten unveraendert (Soft-Disable); mit Haekchen braucht es einen zweiten Klick,
 * weil die Loeschung unwiderruflich ist. Die Entscheidungslogik liegt in
 * `lib/uninstall-flow.ts`, damit sie ohne DOM testbar ist.
 *
 * Nach dem Klick, angelehnt an den `uninstallFromErp`-Helper des
 * @vario-software/vario-app-framework-frontend:
 *   1. Server-Zustand abbauen (`kaufland_uninstall` RPC).
 *   2. Nur bei Haekchen: `kaufland_purge_data` — Reihenfolge bewusst so, damit bei einem
 *      Abbruch immerhin die Verbindung deaktiviert und die Secrets vernichtet sind.
 *   3. `{ requestUninstall: true }` per postMessage an den Vario-Host.
 *   4. Auf `{ uninstallState: true|false }` warten.
 *      `true` ⇒ Vario hat die App entfernt. `false` ⇒ Vario hat abgelehnt.
 *
 * Geloescht wird vor Varios Bestaetigung, weil Vario den iframe danach schliessen kann und
 * der Aufruf dann nie ankaeme.
 *
 * Sending `installationComplete` (which we tried earlier) is the install flow's success message
 * and does NOT trigger Vario's uninstall path — that was the reason the app was not actually
 * being removed.
 *
 * Darstellung: derselbe kf-page-Dialog-Rahmen wie `/#/install` (App-Header + schwebende Karte,
 * Vario-grüner Akzent), Höhe per Auto-Grow (`ensureKfDialogShell` + `initVarioIframeHeight('auto')`
 * in main.ts) — kein Vollflächen-Slot, daher kein `useFullHeight`.
 */
export async function renderUninstall(ctx: UninstallContext) {
  ensureKfDialogShell();
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="kf-page kf-dialog">
      <main class="main">
        <header class="apphead">
          <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
          <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
          <span class="hdivider"></span>
          <div class="htitle">
            <div class="heyebrow">Kaufland · Deinstallation</div>
            <h1>Kaufland-App deinstallieren</h1>
          </div>
          <div class="hactions">
            <span class="hstatus" id="kl-uninst-pill"><span class="d"></span>Bereit</span>
          </div>
        </header>

        <div class="card" id="kl-uninst-card">
          <p class="dlg-intro">Die Verbindung zu <b>Kaufland</b> wird deaktiviert und die gespeicherten
            Zugangsdaten werden vernichtet. Deine <b>Konfiguration bleibt erhalten</b> — Marktplatz-Zuordnungen
            und Kanal-Verknüpfungen sind nach einer erneuten Installation sofort wieder da.</p>

          <label class="dlg-hint" id="kl-purge-label" style="display:flex;gap:.55em;align-items:flex-start;cursor:pointer;">
            <input type="checkbox" id="kl-purge-cb" style="margin-top:.2em;cursor:pointer;width:16px;height:16px;">
            <span>Alle Kaufland-Daten dieses Mandanten unwiderruflich löschen</span>
          </label>

          <div class="dlg-msg" id="kl-purge-warn" hidden></div>

          <div class="dlg-foot">
            <button type="button" class="btn btn-primary" id="kl-uninst-go">Deinstallieren</button>
          </div>

          <div id="kl-progress" hidden>
            <div class="sec-lab">Fortschritt</div>
            <div class="dlg-steps">
              <div class="dlg-step" data-step="server">
                <span class="dlg-step-ic"></span>
                <span class="dlg-step-txt">Verbindung deaktivieren, Zugangsdaten vernichten</span>
              </div>
              <div class="dlg-step" data-step="purge" hidden>
                <span class="dlg-step-ic"></span>
                <span class="dlg-step-txt">Alle Kaufland-Daten löschen</span>
              </div>
              <div class="dlg-step" data-step="vario">
                <span class="dlg-step-ic"></span>
                <span class="dlg-step-txt">App in Vario entfernen</span>
              </div>
            </div>
          </div>

          <div class="dlg-msg" id="uninstall-status" aria-live="polite"></div>
        </div>
      </main>
    </div>
  `;

  const card = ctx.contentEl.querySelector<HTMLDivElement>('#kl-uninst-card')!;
  const pill = ctx.contentEl.querySelector<HTMLSpanElement>('#kl-uninst-pill')!;
  const statusEl = ctx.contentEl.querySelector<HTMLDivElement>('#uninstall-status')!;

  function setStatus(text: string, kind: 'info' | 'ok' | 'error' = 'info') {
    if (!text) {
      statusEl.className = 'dlg-msg';
      statusEl.innerHTML = '';
      return;
    }
    const icon = kind === 'ok' ? KF_DLG_ICON.check : kind === 'error' ? KF_DLG_ICON.alert : KF_DLG_ICON.info;
    statusEl.className = `dlg-msg show ${kind}`;
    statusEl.innerHTML = `${icon}<span>${kfEscapeHtml(text)}</span>`;
  }

  function setPill(text: string, kind: 'pending' | 'ok' | 'error') {
    pill.className = kind === 'ok' ? 'hstatus' : `hstatus ${kind}`;
    pill.innerHTML = `<span class="d"></span>${kfEscapeHtml(text)}`;
  }

  function setStep(step: 'server' | 'purge' | 'vario', state: 'running' | 'done' | 'failed') {
    const el = ctx.contentEl.querySelector<HTMLDivElement>(`.dlg-step[data-step="${step}"]`);
    if (!el) return;
    el.className = `dlg-step ${state}`;
    el.querySelector<HTMLElement>('.dlg-step-ic')!.innerHTML =
      state === 'running' ? '<span class="dlg-spin"></span>'
      : state === 'done' ? KF_DLG_ICON.check
      : KF_ICON.close;
  }

  // ---- Auswahl statt Automatik -------------------------------------------
  // Frueher lief der Slot ohne Interaktion durch. Seit der Loesch-Option wartet
  // er auf die Entscheidung des Kunden.
  const cb = ctx.contentEl.querySelector<HTMLInputElement>('#kl-purge-cb')!;
  const warn = ctx.contentEl.querySelector<HTMLDivElement>('#kl-purge-warn')!;
  const goBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#kl-uninst-go')!;
  const progress = ctx.contentEl.querySelector<HTMLDivElement>('#kl-progress')!;

  let state: UninstallState = createUninstallState();
  let preview: PurgePreview | null = null;

  // Zahlen im Hintergrund holen. Schlaegt das fehl, bleibt der Dialog benutzbar —
  // die Warnung nennt dann keine Zahlen, statt den ganzen Slot zu blockieren.
  void (async () => {
    try {
      const { data, error } = await ctx.supabase.rpc('kaufland_purge_preview');
      if (!error) preview = (Array.isArray(data) ? data[0] : data) as PurgePreview;
    } catch {
      preview = null;
    }
    if (state.purgeChecked) renderWarn();
  })();

  function renderWarn() {
    if (!state.purgeChecked) {
      warn.hidden = true;
      warn.innerHTML = '';
      return;
    }
    const teile = preview
      ? [
          `${preview.orders.toLocaleString('de-DE')} Bestellungen`,
          `${preview.units.toLocaleString('de-DE')} Angebote`,
          `${preview.listings.toLocaleString('de-DE')} Lister-Einträge`,
          `${preview.storefronts} Storefronts samt Lagerzuordnung`,
          `${preview.article_mappings.toLocaleString('de-DE')} Artikelzuordnungen`,
        ].join(', ')
      : 'alle Bestellungen, Angebote, Lister-Einträge und Storefronts';
    warn.hidden = false;
    warn.className = 'dlg-msg show error';
    warn.innerHTML = `${KF_DLG_ICON.alert}<span>Es werden <b>${kfEscapeHtml(teile)}</b> gelöscht.
      Das lässt sich nicht rückgängig machen. Dein Vario-Zugang und deine Benutzer bleiben bestehen.</span>`;
  }

  cb.addEventListener('change', () => {
    state = setPurgeChecked(state, cb.checked);
    renderWarn();
    goBtn.textContent = primaryLabel(state);
    goBtn.classList.toggle('btn-danger', state.purgeChecked);
    goBtn.classList.toggle('btn-primary', !state.purgeChecked);
  });

  goBtn.addEventListener('click', () => {
    const r = pressPrimary(state);
    state = r.state;
    goBtn.textContent = primaryLabel(state);
    if (r.action === 'none') return; // erster Klick: nur geschaerft
    cb.disabled = true;
    goBtn.disabled = true;
    progress.hidden = false;
    void runUninstall(r.action === 'purge-and-uninstall');
  });

  async function runUninstall(purge: boolean) {
    setPill('Deinstallation läuft …', 'pending');
    setStep('server', 'running');

    // ---- 1. Verbindung deaktivieren, Zugangsdaten vernichten --------------
    let serverOk = true;
    try {
      const { data, error } = await ctx.supabase.rpc('kaufland_uninstall');
      if (error) {
        serverOk = false;
        setStep('server', 'failed');
        setStatus(`Server-Cleanup fehlgeschlagen: ${error.message}`, 'error');
      } else {
        const r = (Array.isArray(data) ? data[0] : data) as UninstallResult | undefined;
        setStep('server', 'done');
        if (r?.ok) {
          const n = r.removed_storefronts_count ?? 0;
          setStatus(
            n > 0 && !purge
              ? `Marktplatz-Verbindung deaktiviert (Konfiguration inkl. ${n} Storefront-Verknüpfungen bleibt erhalten).`
              : 'Marktplatz-Verbindung deaktiviert.',
            'info',
          );
        } else {
          setStatus('Keine Verbindung gespeichert — Vario-Status wird zurückgesetzt.', 'info');
        }
      }
    } catch (e) {
      serverOk = false;
      setStep('server', 'failed');
      setStatus(`Server-Cleanup-Ausnahme: ${(e as Error).message}`, 'error');
    }

    // ---- 2. Daten loeschen, nur auf ausdruecklichen Wunsch ----------------
    let purgeOk = true;
    if (purge) {
      const stepEl = ctx.contentEl.querySelector<HTMLDivElement>('.dlg-step[data-step="purge"]')!;
      stepEl.hidden = false;
      setStep('purge', 'running');
      try {
        const { data, error } = await ctx.supabase.rpc('kaufland_purge_data');
        if (error) {
          purgeOk = false;
          setStep('purge', 'failed');
          setStatus(`Löschen fehlgeschlagen: ${error.message}`, 'error');
        } else {
          const r = (Array.isArray(data) ? data[0] : data) as { total_rows?: number } | undefined;
          setStep('purge', 'done');
          setStatus(`${(r?.total_rows ?? 0).toLocaleString('de-DE')} Datensätze gelöscht.`, 'info');
        }
      } catch (e) {
        purgeOk = false;
        setStep('purge', 'failed');
        setStatus(`Löschen fehlgeschlagen: ${(e as Error).message}`, 'error');
      }
    }

    // ---- 3. Vario bitten, die App zu entfernen ----------------------------
    setStep('vario', 'running');
    const ok = await requestVarioUninstall();
    setStep('vario', ok ? 'done' : 'failed');

    if (ok) {
      setPill('Deinstalliert', 'ok');
      showKfDialogResult(card, {
        kind: purge && purgeOk ? 'error' : 'neutral',
        icon: KF_DLG_ICON.trash,
        title: 'Kaufland-App wurde deinstalliert',
        text: !serverOk
          ? 'Die App ist in Vario entfernt, unser Server-Cleanup hat dabei aber einen Fehler gemeldet. Bitte informiere den Support, bevor du die App neu installierst.'
          : purge && !purgeOk
            ? 'Die App ist entfernt, das Löschen der Daten ist aber fehlgeschlagen. Deine Daten sind noch vorhanden — bitte wende dich an den Support.'
            : purge
              ? 'Die App ist entfernt und alle Kaufland-Daten wurden gelöscht. Dein Vario-Zugang bleibt bestehen.'
              : 'Die Verbindung ist deaktiviert und die Zugangsdaten sind vernichtet. Deine Konfiguration bleibt gespeichert und wird bei einer erneuten Installation wieder genutzt.',
        note: purge && purgeOk ? 'Daten unwiderruflich gelöscht' : purge ? undefined : 'Konfiguration bleibt erhalten',
      });
    } else {
      setPill('Abgelehnt', 'error');
      showKfDialogResult(card, {
        kind: 'error',
        icon: KF_ICON.ban,
        title: 'Vario hat die Deinstallation abgelehnt',
        text: purge && purgeOk
          ? 'Die App ist weiterhin installiert, deine Kaufland-Daten wurden aber bereits gelöscht. Bitte wende dich an den Support, bevor du weiterarbeitest.'
          : 'Die App ist weiterhin installiert. Bitte versuche es später erneut oder kontaktiere den Support.',
      });
    }
  }
}

function requestVarioUninstall(): Promise<boolean> {
  return new Promise((resolve) => {
    const listener = (e: MessageEvent) => {
      const data = e.data as { uninstallState?: boolean } | null;
      if (!data || typeof data !== 'object' || typeof data.uninstallState !== 'boolean') return;
      window.removeEventListener('message', listener);
      resolve(data.uninstallState);
    };
    window.addEventListener('message', listener);

    // 30s safety timeout — if Vario never answers, fail closed.
    setTimeout(() => {
      window.removeEventListener('message', listener);
      resolve(false);
    }, 30_000);

    sendVarioMessage({ requestUninstall: true });
  });
}
